import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getSupabaseClient } from '@/lib/supabase';

interface CategoryData {
  id: string;
  nameKey: string;
  count: number;
  image: string;
}

const CATEGORY_KEYWORDS: Record<string, string[]> = {
  airless: ['에어리스'],
  pump: ['펌프'],
  spray: ['스프레이', '스프레이어'],
  dropper: ['드롭퍼', '스포이드', '스포이트'],
  cream: ['크림', '단지', 'jar'],
  foam: ['폼', 'foam'],
  tubes: ['튜브', 'tube'],
  caps: ['캡', '캡만', '펌프헤드', '스프레이캡'],
  sample: ['샘플', 'sample', '소량'],
  aluminum: ['알루미늄', '금속'],
  glass: ['유리', '글라스'],
  pet: ['PET', 'PETG'],
  cushion: ['쿠션', '컴팩트', 'compact'],
  lip: ['립', '립스틱', 'lipstick', '메이크업'],
  refill: ['리필', 'refill'],
  eco: ['에코', '친환경', '바이오', 'bamboo', 'eco'],
};

const CATEGORY_FALLBACK_IMAGES: Record<string, string> = {
  airless: 'https://readdy.ai/api/search-image?query=A%20sleek%20airless%20pump%20bottle%20cosmetic%20packaging%20with%20a%20matte%20white%20body%20and%20minimal%20design%20standing%20on%20a%20clean%20pure%20white%20studio%20background%20with%20soft%20diffused%20lighting%20and%20gentle%20shadow%20for%20elegant%20beauty%20product%20photography&width=800&height=600&seq=cat-airless-01&orientation=landscape',
  pump: 'https://readdy.ai/api/search-image?query=A%20modern%20lotion%20pump%20bottle%20for%20cosmetic%20skincare%20with%20a%20transparent%20acrylic%20container%20and%20white%20dispenser%20pump%20placed%20on%20a%20clean%20white%20studio%20surface%20with%20soft%20natural%20light%20and%20minimal%20beauty%20product%20photography&width=800&height=600&seq=cat-pump-01&orientation=landscape',
  spray: 'https://readdy.ai/api/search-image?query=A%20fine%20mist%20spray%20bottle%20cosmetic%20packaging%20with%20a%20clear%20glass%20body%20and%20silver%20sprayer%20head%20on%20a%20bright%20clean%20white%20background%20with%20soft%20studio%20lighting%20and%20subtle%20reflection%20for%20elegant%20product%20photography&width=800&height=600&seq=cat-spray-01&orientation=landscape',
  dropper: 'https://readdy.ai/api/search-image?query=An%20elegant%20amber%20glass%20dropper%20bottle%20for%20serum%20cosmetic%20packaging%20with%20a%20black%20rubber%20cap%20on%20a%20clean%20white%20studio%20background%20with%20soft%20warm%20lighting%20and%20minimal%20premium%20beauty%20product%20photography&width=800&height=600&seq=cat-dropper-01&orientation=landscape',
  cream: 'https://readdy.ai/api/search-image?query=A%20frosted%20white%20glass%20cream%20jar%20cosmetic%20packaging%20with%20a%20simple%20screw%20lid%20on%20a%20clean%20pure%20white%20studio%20background%20with%20soft%20diffused%20lighting%20and%20gentle%20shadows%20for%20minimal%20skincare%20product%20photography&width=800&height=600&seq=cat-cream-01&orientation=landscape',
  foam: 'https://readdy.ai/api/search-image?query=A%20white%20foam%20pump%20bottle%20cosmetic%20dispenser%20with%20a%20clean%20minimal%20design%20on%20a%20bright%20white%20studio%20background%20with%20soft%20natural%20lighting%20and%20subtle%20shadow%20for%20modern%20skincare%20product%20photography&width=800&height=600&seq=cat-foam-01&orientation=landscape',
  tubes: 'https://readdy.ai/api/search-image?query=A%20soft%20squeeze%20cosmetic%20tube%20packaging%20with%20a%20flip%20top%20cap%20in%20clean%20pastel%20tone%20on%20a%20pure%20white%20studio%20background%20with%20soft%20diffused%20lighting%20for%20minimal%20beauty%20product%20photography&width=800&height=600&seq=cat-tubes-01&orientation=landscape',
  caps: 'https://readdy.ai/api/search-image?query=An%20assortment%20of%20cosmetic%20bottle%20caps%20and%20closures%20in%20black%20white%20and%20gold%20finishes%20arranged%20neatly%20on%20a%20clean%20white%20studio%20background%20with%20soft%20lighting%20for%20minimal%20packaging%20component%20photography&width=800&height=600&seq=cat-caps-01&orientation=landscape',
  sample: 'https://readdy.ai/api/search-image?query=A%20collection%20of%20mini%20sample%20cosmetic%20packaging%20including%20small%20bottles%20jars%20and%20sachets%20arranged%20on%20a%20clean%20white%20studio%20background%20with%20soft%20lighting%20for%20minimal%20beauty%20product%20photography&width=800&height=600&seq=cat-sample-01&orientation=landscape',
  aluminum: 'https://readdy.ai/api/search-image?query=A%20sleek%20silver%20aluminum%20cosmetic%20bottle%20packaging%20with%20brushed%20metal%20finish%20and%20minimal%20design%20on%20a%20clean%20white%20studio%20background%20with%20soft%20reflective%20lighting%20for%20premium%20beauty%20product%20photography&width=800&height=600&seq=cat-aluminum-01&orientation=landscape',
  glass: 'https://readdy.ai/api/search-image?query=An%20elegant%20clear%20glass%20cosmetic%20bottle%20for%20skincare%20packaging%20with%20a%20minimal%20silver%20cap%20on%20a%20clean%20white%20studio%20background%20with%20soft%20natural%20lighting%20and%20gentle%20shadow%20for%20luxury%20beauty%20photography&width=800&height=600&seq=cat-glass-01&orientation=landscape',
  pet: 'https://readdy.ai/api/search-image?query=A%20clear%20PET%20plastic%20cosmetic%20bottle%20packaging%20with%20transparent%20body%20and%20white%20cap%20on%20a%20clean%20pure%20white%20studio%20background%20with%20soft%20diffused%20lighting%20for%20minimal%20beauty%20product%20photography&width=800&height=600&seq=cat-pet-01&orientation=landscape',
  cushion: 'https://readdy.ai/api/search-image?query=A%20round%20cushion%20compact%20case%20makeup%20packaging%20with%20a%20glossy%20white%20finish%20and%20mirror%20interior%20on%20a%20clean%20white%20studio%20background%20with%20soft%20lighting%20for%20elegant%20beauty%20product%20photography&width=800&height=600&seq=cat-cushion-01&orientation=landscape',
  lip: 'https://readdy.ai/api/search-image?query=An%20elegant%20lipstick%20tube%20makeup%20packaging%20with%20a%20sleek%20metallic%20body%20and%20minimal%20design%20on%20a%20clean%20white%20studio%20background%20with%20soft%20diffused%20lighting%20for%20premium%20beauty%20product%20photography&width=800&height=600&seq=cat-lip-01&orientation=landscape',
  refill: 'https://readdy.ai/api/search-image?query=A%20refillable%20cosmetic%20container%20with%20replaceable%20inner%20bottle%20and%20sustainable%20minimal%20design%20on%20a%20clean%20white%20studio%20background%20with%20soft%20natural%20lighting%20for%20eco%20friendly%20beauty%20photography&width=800&height=600&seq=cat-refill-01&orientation=landscape',
  eco: 'https://readdy.ai/api/search-image?query=An%20eco%20friendly%20cosmetic%20packaging%20made%20of%20bamboo%20and%20natural%20sustainable%20materials%20with%20minimal%20design%20on%20a%20clean%20white%20studio%20background%20with%20soft%20lighting%20for%20green%20beauty%20product%20photography&width=800&height=600&seq=cat-eco-01&orientation=landscape',
};

function mapCategory(name: string): string | null {
  const lower = name.toLowerCase();
  for (const [cat, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    if (keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      return cat;
    }
  }
  return null;
}

export default function CategoriesSection() {
  const { t } = useTranslation('home');
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryImageOverrides, setCategoryImageOverrides] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    async function fetchCategories() {
      setLoading(true);
      try {
        const supabase = getSupabaseClient();
        if (!supabase) {
          setLoading(false);
          return;
        }

        const [productsResult, settingsResult] = await Promise.all([
          supabase.from('products').select('id, name, tags'),
          supabase.from('site_settings').select('key, value').eq('key', 'home_category_images').maybeSingle(),
        ]);

        // Parse admin-managed category image overrides
        const overrides: Record<string, string> = {};
        const settingValue = settingsResult?.data?.value;
        if (Array.isArray(settingValue)) {
          settingValue.forEach((item) => {
            if (
              item &&
              typeof item === 'object' &&
              typeof item.id === 'string' &&
              typeof item.url === 'string'
            ) {
              overrides[item.id] = item.url;
            }
          });
        }
        if (!cancelled) setCategoryImageOverrides(overrides);

        const { data: products, error } = productsResult;
        if (error || !products) {
          setLoading(false);
          return;
        }

        // Group products by mapped category
        const categoryMap: Record<string, { ids: string[]; firstImage: string }> = {};

        products.forEach((p) => {
          const cat = mapCategory(p.name);
          if (!cat) return;

          if (!categoryMap[cat]) {
            const tags = (p.tags as Record<string, unknown>) || {};
            const imageUrl =
              (tags.image_url as string) ||
              (tags.source_image_url as string) ||
              '';
            categoryMap[cat] = { ids: [p.id], firstImage: imageUrl };
          } else {
            categoryMap[cat].ids.push(p.id);
          }
        });

        // Build category list sorted by count desc
        const built = Object.entries(categoryMap)
          .map(([cat, info]) => ({
            id: cat,
            nameKey: `categories.${cat}`,
            count: info.ids.length,
            image: info.firstImage,
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 8);

        if (!cancelled) {
          setCategories(built);
        }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchCategories();
    return () => { cancelled = true; };
  }, []);

  // Show a clean skeleton while loading or empty state if no data
  const displayCategories =
    categories.length > 0
      ? categories
      : loading
      ? []
      : [];

  return (
    <section className="w-full bg-cream-50 py-12 md:py-16 lg:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col items-center text-center mb-8 md:mb-10 gap-4">
            <div>
              <span className="text-xs font-medium text-warm-500 uppercase tracking-[0.15em] mb-3 block">
                {t('categories.subtitle') || 'Our PRODUCTS'}
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-700">
                {t('categories.title') || 'Search by Category'}
              </h2>
            </div>
            <a
              href="/packaging"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-warm-500 transition-colors whitespace-nowrap"
            >
              {t('categories.viewAll') || 'View All Products'}
              <i className="ri-arrow-right-line" />
            </a>
          </div>

          {/* Loading Skeleton */}
          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl overflow-hidden bg-white border border-ink-100"
                >
                  <div className="aspect-[4/3] bg-ink-100 animate-pulse" />
                </div>
              ))}
            </div>
          )}

          {/* Empty state: no products found */}
          {!loading && displayCategories.length === 0 && (
            <div className="flex flex-col items-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-cream-100 flex items-center justify-center mb-4">
                <i className="ri-inbox-line text-ink-300 text-2xl" />
              </div>
              <p className="text-sm text-ink-400">{t('categories.empty') || '등록된 제품이 없습니다.'}</p>
            </div>
          )}

          {/* Category Grid */}
          {!loading && displayCategories.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {displayCategories.map((cat) => (
                <a
                  key={cat.id}
                  href={`/packaging?category=${cat.id}`}
                  className="group block rounded-xl overflow-hidden bg-white border border-ink-100 transition-all duration-300 hover:border-warm-400/30 hover:shadow-lg hover:shadow-warm-500/5"
                >
                  <div className="aspect-[4/3] relative overflow-hidden">
                    <img
                      src={categoryImageOverrides[cat.id] || cat.image || CATEGORY_FALLBACK_IMAGES[cat.id]}
                      alt={t(cat.nameKey, cat.id)}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/80 via-ink-900/20 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
                      <h3 className="text-lg md:text-xl font-bold text-white leading-tight">
                        {t(cat.nameKey, cat.id)}
                      </h3>
                      <p className="text-xs text-white/60 mt-1">
                        {cat.count.toLocaleString()}+ {t('categories.browse') || 'products'}
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}